<template>
  <div v-loading="loading" class="mp-layout">
    <MarketPortalSidebar :active-category-id="categoryId" />
    <div class="mp-main">
      <div class="mp-panel">
        <div class="mp-crumb">
          <span class="mp-crumb__link" @click="goto('market')">活动市场</span>
          ›
          <span class="mp-crumb__cur">{{ crumbTitle }}</span>
        </div>
        <MarketFilterBar :show-industry="false" />
        <div class="mp-sort-bar">
          <span class="mp-sort-label">排序：</span>
          <span
            v-for="item in sortOptions"
            :key="item.mode"
            class="mp-sort-item"
            :class="{ 'is-active': sortMode === item.mode }"
            @click="sortMode = item.mode"
          >
            {{ item.label }}
          </span>
          <span class="mp-sort-count">共 {{ totalCount }} 个模板</span>
        </div>
        <MarketTemplateGrid :ids="pageIds" :columns="5" @preview="openPreview" @use="openUse" />
        <div class="mp-pager">
          <span class="mp-pg" :class="{ 'is-disabled': currentPage === 0 }" @click="prevPage">‹</span>
          <span
            v-for="p in totalPages"
            :key="p"
            class="mp-pg"
            :class="{ 'is-cur': currentPage === p - 1 }"
            @click="currentPage = p - 1"
          >
            {{ p }}
          </span>
          <span class="mp-pg" :class="{ 'is-disabled': currentPage >= totalPages - 1 }" @click="nextPage">
            ›
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useMarketPortal } from '../useMarketPortal'
import { useMarketPortalStore } from '../useMarketPortalStore'
import MarketFilterBar from '../components/MarketFilterBar.vue'
import MarketPortalSidebar from '../components/MarketPortalSidebar.vue'
import MarketTemplateGrid from '../components/MarketTemplateGrid.vue'

defineOptions({ name: 'MarketCategoryPage' })

const emit = defineEmits<{
  preview: [id: number]
  use: [id: number]
}>()

const { categoryId, goto } = useMarketPortal()
const { loading, categories, loadCategoryPage } = useMarketPortalStore()

const sortMode = ref<0 | 1 | 2>(0)
const currentPage = ref(0)
const pageIds = ref<number[]>([])
const totalCount = ref(0)

const sortOptions = [
  { mode: 0 as const, label: '默认排序' },
  { mode: 1 as const, label: '最热优先' },
  { mode: 2 as const, label: '最新上架' }
]

const crumbTitle = computed(() => {
  if (categoryId.value === -1) return '节日模板'
  return categories.value.find((c) => c.id === categoryId.value)?.name || '分类'
})

const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / 10)))

const fetchPage = async () => {
  const result = await loadCategoryPage({
    categoryId: categoryId.value,
    sortMode: sortMode.value,
    pageNo: currentPage.value + 1,
    pageSize: 10
  })
  pageIds.value = result.ids
  totalCount.value = result.total
}

watch([categoryId, sortMode, currentPage], fetchPage, { immediate: true })

watch([categoryId, sortMode], () => {
  currentPage.value = 0
})

const prevPage = () => {
  if (currentPage.value > 0) currentPage.value--
}

const nextPage = () => {
  if (currentPage.value < totalPages.value - 1) currentPage.value++
}

const openPreview = (id: number) => emit('preview', id)
const openUse = (id: number) => emit('use', id)
</script>

<style lang="scss" scoped>
.mp-layout {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.mp-main {
  flex: 1;
  min-width: 0;
}

.mp-panel {
  background: #fff;
  border-radius: 8px;
  padding: 12px 16px;
}

.mp-crumb {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #909399;
  font-size: 13px;
  margin-bottom: 14px;

  &__link {
    cursor: pointer;

    &:hover {
      color: #2b6de8;
    }
  }

  &__cur {
    color: #303133;
    font-weight: 600;
  }
}

.mp-sort-bar {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 8px 0 12px;
  border-bottom: 1px solid #f0f1f4;
  margin: 10px 0 12px;
}

.mp-sort-label {
  color: #909399;
  font-size: 13px;
}

.mp-sort-item {
  font-size: 13px;
  color: #606266;
  cursor: pointer;

  &.is-active {
    color: #2b6de8;
    font-weight: 600;
  }
}

.mp-sort-count {
  margin-left: auto;
  color: #b0b3ba;
  font-size: 12px;
}

.mp-pager {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding: 22px 0 4px;
}

.mp-pg {
  min-width: 30px;
  height: 30px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  color: #606266;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #fff;
  padding: 0 8px;

  &.is-cur {
    background: #2b6de8;
    border-color: #2b6de8;
    color: #fff;
  }

  &.is-disabled {
    color: #c9ccd4;
    cursor: not-allowed;
  }
}
</style>
